import { useCallback, useRef, useState, ChangeEvent, useEffect } from "react";
import { Button, Flex } from "@radix-ui/themes";
import { UpdateIcon } from "@radix-ui/react-icons";
import { toast } from "sonner";
import { useUploadSnippetAttachment } from "@/v2/entities/KnowledgeSnippet";
import { Video02Icon } from "@/v2/assets/icons/Video02Icon";
import { ImageAddIcon } from "@/v2/assets/icons/ImageAddIcon";
import styles from "./FloatingMediaButtons.module.scss";

interface FloatingMediaButtonsProps {
  snippetId: string;
  onAddImage: (url: string) => void;
  onAddVideo: (url: string) => void;
}

const ACCEPTED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"];
const ACCEPTED_VIDEO_TYPES = ["video/mp4", "video/webm"];
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100MB

function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  } else if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(2)} KB`;
  } else {
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }
}

export function FloatingMediaButtons({
  snippetId,
  onAddImage,
  onAddVideo,
}: FloatingMediaButtonsProps) {
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const buttonsRef = useRef<HTMLDivElement>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const { mutateAsync: uploadAttachment } = useUploadSnippetAttachment();

  useEffect(() => {
    if (!buttonsRef.current) return;

    const checkDialog = () => {
      const dialog = buttonsRef.current?.closest('[class*="DialogContent"]');
      if (dialog) {
        buttonsRef.current?.classList.add(styles.inDialog);
      } else {
        buttonsRef.current?.classList.remove(styles.inDialog);
        const container = document.querySelector(
          '[class*="snippetDetailContainer"]'
        ) as HTMLElement;
        if (container && buttonsRef.current) {
          const rect = container.getBoundingClientRect();
          buttonsRef.current.style.left = `${rect.left + rect.width / 2}px`;
        }
      }
    };

    checkDialog();
    const observer = new MutationObserver(checkDialog);
    observer.observe(document.body, { childList: true, subtree: true });

    window.addEventListener("resize", checkDialog);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", checkDialog);
    };
  }, []);

  const handleImageFileSelect = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }

      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        toast.error("Please select an image file (PNG, JPEG, or WebP)");
        return;
      }

      if (file.size > MAX_IMAGE_SIZE) {
        toast.error(
          `Image size must be less than 10MB. Your file is ${formatFileSize(
            file.size
          )}.`
        );
        return;
      }

      try {
        setIsUploadingImage(true);

        const uploadPromise = uploadAttachment({
          snippetId,
          file,
        });

        toast.promise(uploadPromise, {
          loading: "Uploading image…",
          success: "Image uploaded successfully",
          error: "Failed to upload image. Please try again.",
        });

        const publicUrl = await uploadPromise;
        onAddImage(publicUrl);
      } catch (error) {
        console.error("Failed to upload image:", error);
      } finally {
        setIsUploadingImage(false);
        if (imageInputRef.current) {
          imageInputRef.current.value = "";
        }
      }
    },
    [snippetId, uploadAttachment, onAddImage]
  );

  const handleVideoFileSelect = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }

      if (!ACCEPTED_VIDEO_TYPES.includes(file.type)) {
        toast.error("Please select a video file (MP4 or WebM)");
        return;
      }

      if (file.size > MAX_VIDEO_SIZE) {
        toast.error(
          `Video size must be less than 100MB. Your file is ${formatFileSize(
            file.size
          )}.`
        );
        return;
      }

      try {
        setIsUploadingVideo(true);

        const uploadPromise = uploadAttachment({
          snippetId,
          file,
        });

        toast.promise(uploadPromise, {
          loading: "Uploading video…",
          success: "Video uploaded successfully",
          error: "Failed to upload video. Please try again.",
        });

        const publicUrl = await uploadPromise;
        onAddVideo(publicUrl);
      } catch (error) {
        console.error("Failed to upload video:", error);
      } finally {
        setIsUploadingVideo(false);
        if (videoInputRef.current) {
          videoInputRef.current.value = "";
        }
      }
    },
    [snippetId, uploadAttachment, onAddVideo]
  );

  const handleImageButtonClick = useCallback(() => {
    imageInputRef.current?.click();
  }, []);

  const handleVideoButtonClick = useCallback(() => {
    videoInputRef.current?.click();
  }, []);

  return (
    <>
      <input
        ref={imageInputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES.join(",")}
        onChange={handleImageFileSelect}
        style={{ display: "none" }}
      />
      <input
        ref={videoInputRef}
        type="file"
        accept={ACCEPTED_VIDEO_TYPES.join(",")}
        onChange={handleVideoFileSelect}
        style={{ display: "none" }}
      />
      <Flex ref={buttonsRef} className={styles.floatingButtons} gap="2">
        <Button
          variant="ghost"
          highContrast
          size="3"
          onClick={handleImageButtonClick}
          disabled={isUploadingImage}
        >
          {isUploadingImage ? (
            <UpdateIcon width="16" height="16" className="animate-spin" />
          ) : (
            <ImageAddIcon size={16} />
          )}
          Insert Image
        </Button>
        <Button
          variant="ghost"
          highContrast
          size="3"
          onClick={handleVideoButtonClick}
          disabled={isUploadingVideo}
        >
          {isUploadingVideo ? (
            <UpdateIcon width="16" height="16" className="animate-spin" />
          ) : (
            <Video02Icon size={16} />
          )}
          Insert Video
        </Button>
      </Flex>
    </>
  );
}
